import { Box, Skeleton, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { customerDeliveryDownloadFileDetailService } from '@services/private/operation-history/customer-delivery/download-file-detail.service';
import { externalDeliveryDownloadFileDetailService } from '@services/private/operation-history/external-delivery/download-file-detail.service';
import { is200xRange } from '@utils/utilities';
import { useEffect, useState } from 'react';

import FieldBox from 'shared/components/widgets/field-box';
import FieldBoxProperty from 'shared/components/widgets/field-box-property';
import { bytesToSize } from 'shared/helpers/bytes-to-size';
import { getClassification } from 'shared/helpers/external-delivery/get-classification';
import { getDepartment } from 'shared/helpers/external-delivery/get-department';
import { AdminConfigEnv, Classification, DepartmentCode } from 'types';
import MultipleFieldBox from '../../../shared/components/widgets/multiple-field-box';

type CustomerDownloadFileData = {
  adminConfigEnv: {
    cssJsVersion: string;
  } | null;
  otmFileHistDetail: {
    fileTotalCnt: number;
    fileTotalSize: number;
    histDt: string;
    histSeq: string;
    remoteIp: string;
  } | null;
  otmThenFileList:
  | [
    {
      otmfOriginName: string;
      otmfSize: number;
      regDt: number;
      regName: string;
      regTp: 'admin' | 'staff' | 'user';
      updDt: string | null;
      updName: string | null;
      updTp: string | null;
    },
  ]
  | null;
  otmThenInfo: {
    expirationYmd: string;
    otmSt: 'allow' | 'deny';
    otmSubject: string;
  } | null;
};

type ExternalDownloadFileData = {
  adminConfigEnv: AdminConfigEnv;
  shmFileHistDetail: {
    fileTotalCnt: number;
    fileTotalSize: number;
    histDt: string;
    histSeq: string;
    regDt: string;
    regName: string;
    regTp: 'admin' | 'staff';
    remoteIp: string;
    shmDeptCd: DepartmentCode;
    shmLfsKind: Classification;
    shmSubject: string;
    thenFileJson: null;
    updDt: string;
    updName: string;
    updTp: 'admin';
  } | null;
  shmThenFileList:
  | [
    {
      regDt: string;
      regName: string;
      regTp: 'admin' | 'staff' | 'user';
      shmfOriginName: string;
      shmfSize: number;
    },
  ]
  | null;
  shmThenInfo: {
    shmDeptCd: DepartmentCode;
    shmLfsKind: Classification;
    shmSubject: string;
  } | null;
};

const OperationHistoryDetail = ({ histSeq, type }) => {
  const [isLoading, setLoading] = useState<boolean>(false)
  const [customerDownloadFileData, setCustomerDownloadFileData] =
    useState<CustomerDownloadFileData>({
      adminConfigEnv: null,
      otmFileHistDetail: null,
      otmThenFileList: null,
      otmThenInfo: null,
    });

  const [externalDownloadFileData, setExternalDownloadFileData] =
    useState<ExternalDownloadFileData>({
      adminConfigEnv: null,
      shmFileHistDetail: null,
      shmThenFileList: null,
      shmThenInfo: null,
    });

  const getCustomerDeliveryDownloadFileDetail = async (histSeq: string) => {
    const { response, statusCode } =
      await customerDeliveryDownloadFileDetailService(histSeq);

    if (response?.code === '1' && is200xRange(statusCode)) {
      handleCustomerListData([
        { key: 'adminConfigEnv', value: response?.result?.adminConfigEnv },
        {
          key: 'otmFileHistDetail',
          value: response?.result?.otmFileHistDetail,
        },
        { key: 'otmThenFileList', value: response?.result?.otmThenFileList },
        { key: 'otmThenInfo', value: response?.result?.otmThenInfo },
      ]);
    }
  };
  const getExternalDeliveryDownloadFileDetail = async (histSeq: string) => {
    const { response, statusCode } =
      await externalDeliveryDownloadFileDetailService(histSeq);

    if (response?.code === '1' && is200xRange(statusCode)) {
      handleExternalListData([
        { key: 'adminConfigEnv', value: response?.result?.adminConfigEnv },
        {
          key: 'shmFileHistDetail',
          value: response?.result?.shmFileHistDetail,
        },
        {
          key: 'shmThenFileList',
          value: response?.result?.shmThenFileList,
        },
        {
          key: 'shmThenInfo',
          value: response?.result?.shmThenInfo,
        },
      ]);
    }
  };

  const handleCustomerListData = (params) => {
    setCustomerDownloadFileData((prevListData) => {
      const newListData = { ...prevListData };
      params.forEach((item) => {
        newListData[item.key] = item.value;
      });
      return newListData;
    });
  };

  const handleExternalListData = (params) => {
    setExternalDownloadFileData((prevListData) => {
      const newListData = { ...prevListData };
      params.forEach((item) => {
        newListData[item.key] = item.value;
      });
      return newListData;
    });
  };

  useEffect(() => {
    const fetchDetailData = async (histSeq) => {
      setLoading(true)
      if (type === 'CUSTOMER_DELIVERY_DOWNLOAD_FILES') {
        await getCustomerDeliveryDownloadFileDetail(histSeq);
        setLoading(false)
      } else {
        await getExternalDeliveryDownloadFileDetail(histSeq);
        setLoading(false)
      }
    }
    fetchDetailData(histSeq)
  }, [histSeq]);

  // console.log(">>> 고객전달 : " + JSON.stringify(customerDownloadFileData))
  // console.log(">>> 외부공유 : " + JSON.stringify(externalDownloadFileData))
  // console.log(">>> customerDownloadFileData?.otmThenFileList : " + JSON.stringify(customerDownloadFileData?.otmThenFileList))
  // console.log(">>> externalDownloadFileData?.shmThenFileList : " + JSON.stringify(externalDownloadFileData?.shmThenFileList))

  return (
    <Box
      padding="54px 40px 40px"
      display="flex"
      flexDirection="column"
      gap="42px"
      height="90vh"
      sx={{ overflowY: 'scroll' }}
    >
      {/* MORE INFORMATION */}
      <Typography variant="h6" color="var(--base-black)" textAlign="center">
        상세 정보
      </Typography>
      <Box display="flex" flexDirection="column" gap="30px" position="relative">
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection="column" gap={5}>
            {/* HISTORY INFORMATION */}
            <FieldBox title="이력 정보">
              {/* DOWNLOAD DATE */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="다운로드 일자"
                type="text"
                value={
                  customerDownloadFileData?.otmFileHistDetail?.histDt ||
                  externalDownloadFileData?.shmFileHistDetail?.histDt ||
                  '--'
                }
              />
              {/* CONNECTION IP */}
              <FieldBoxProperty
                isLoading={isLoading}
                name="접속IP"
                type="text"
                value={
                  customerDownloadFileData?.otmFileHistDetail?.remoteIp ||
                  externalDownloadFileData?.shmFileHistDetail?.remoteIp ||
                  '--'
                }
              />
            </FieldBox>
            {type === 'CUSTOMER_DELIVERY_DOWNLOAD_FILES' ? (
              <FieldBox title="고객 전달용 파일 정보">
                {/* UPLOAD TITLE */}
                <FieldBoxProperty
                  isLoading={isLoading}
                  name="업로드 제목"
                  type="text"
                  value={customerDownloadFileData?.otmThenInfo?.otmSubject || '--'}
                />
                {/* FILE NAME */}
                {/*<FieldBoxProperty
                isLoading={isLoading}
                  name="파일명"
                  type="text"
                  value={customerDownloadFileData?.otmThenInfo?.otmSubject || '--'}
                />*/}
                {/* FILE SIZE */}
                {/*<FieldBoxProperty
                isLoading={isLoading}
                  name="파일크기"
                  type="text"
                  value={
                    bytesToSize(
                      customerDownloadFileData?.otmFileHistDetail?.fileTotalSize ||
                        0,
                    ) || '--'
                  }
                />*/}
                {/* FILE STATUS */}
                <FieldBoxProperty
                  isLoading={isLoading}
                  name="파일상태"
                  type="text"
                  value={
                    (customerDownloadFileData?.otmThenInfo?.otmSt === 'allow' &&
                      '다운로드 가능') ||
                    (customerDownloadFileData?.otmThenInfo?.otmSt === 'deny' &&
                      '다운로드 불가') ||
                    '--'
                  }
                />
                {/* EXPIRATION DATE */}
                <FieldBoxProperty
                  isLoading={isLoading}
                  name="만료 일자"
                  type="text"
                  value={
                    customerDownloadFileData?.otmThenInfo?.expirationYmd || '--'
                  }
                />
              </FieldBox>
            ) : (
              <FieldBox title="외부 전달용 파일 정보">
                {/* UPLOAD TITLE */}
                <FieldBoxProperty
                  isLoading={isLoading}
                  name="업로드 제목"
                  type="text"
                  value={externalDownloadFileData?.shmThenInfo?.shmSubject || '--'}
                />
                {/* CLASSIFICATION */}
                <FieldBoxProperty
                  isLoading={isLoading}
                  name="분류"
                  type="text"
                  value={
                    getClassification(
                      externalDownloadFileData?.shmThenInfo?.shmLfsKind,
                    ) || '--'
                  }
                />
                {/* DEPARTMENT */}
                <FieldBoxProperty
                  isLoading={isLoading}
                  name="부서"
                  type="text"
                  value={getDepartment(
                    externalDownloadFileData?.shmThenInfo?.shmDeptCd || '--',
                  )}
                />
                {/* FILE NAME */}
                {/*<FieldBoxProperty
                isLoading={isLoading}
                  name="파일명"
                  type="text"
                  value={externalDownloadFileData?.shmThenInfo?.shmSubject || '--'}
                />*/}
                {/* FILE SIZE */}
                {/*<FieldBoxProperty
                isLoading={isLoading}
                  name="파일크기"
                  type="text"
                  value={bytesToSize(
                    externalDownloadFileData?.shmFileHistDetail?.fileTotalSize || 0,
                  )}
                />*/}
              </FieldBox>
            )}
            <MultipleFieldBox title="업로드 파일 정보">
              {isLoading ?
                <TableRow>
                  <TableCell colSpan={4}>
                    <Skeleton height={30} />
                  </TableCell>
                </TableRow>
                : type === 'CUSTOMER_DELIVERY_DOWNLOAD_FILES'
                  ? (
                    <TableBody>
                      {customerDownloadFileData?.otmThenFileList?.map(
                        (item, index) => {
                          return (
                            <TableRow key={index}>
                              <TableCell>{item?.otmfOriginName}</TableCell>
                              <TableCell>
                                {bytesToSize(Number(item?.otmfSize) || 0)}
                              </TableCell>
                              <TableCell>
                                {item?.regTp === 'admin'
                                  ? '관리자'
                                  : item?.regTp === 'staff'
                                    ? '직원'
                                    : '--'}
                              </TableCell>
                              <TableCell>{item?.regName}</TableCell>
                            </TableRow>
                          );
                        },
                      )}
                    </TableBody>
                  )
                  : (
                    <TableBody>
                      {externalDownloadFileData?.shmThenFileList?.map(
                        (item, index) => {
                          return (
                            <TableRow key={index}>
                              <TableCell>{item?.shmfOriginName}</TableCell>
                              <TableCell>
                                {bytesToSize(Number(item?.shmfSize) || 0)}
                              </TableCell>
                              <TableCell>
                                {item?.regTp === 'admin'
                                  ? '관리자'
                                  : item?.regTp === 'staff'
                                    ? '직원'
                                    : '--'}
                              </TableCell>
                              <TableCell>{item?.regName}</TableCell>
                            </TableRow>
                          );
                        },
                      )}
                    </TableBody>
                  )
              }
            </MultipleFieldBox>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OperationHistoryDetail;
